import { CloudDownloadOutlined } from '@ant-design/icons';
import { EssityDatePicker } from '@components/shared/EssityDateTime';
import { DownloadFile } from '@components/shared/file/FileDownloadFunctions';
import { LoadingView } from '@components/shared/import/LoadingView';
import { IsolatedModal } from '@components/shared/modals/IsolatedModal';
import { PharmacyFinder } from '@components/shared/modals/pharmacyFinder/PharmacyFinder';
import { mapEssityFiltersToSieveFilters } from '@components/shared/paginatedEssityTable/SieveModels';
import { ajaxByUser } from '@helper/api';
import { DateFormat } from '@helper/formatHelpers';
import { useRootData } from '@hooks/hook';
import { getEssityApiClient } from '@services/EssityApi';
import { FileType, PharmacyDto } from '@services/src/models';
import { Button, DatePicker, Dropdown, Menu, Space } from 'antd';
import { CenteredRow } from 'layout/CenteredRow';
import { Moment } from 'moment';
import React, { useState } from 'react';
import { IGridStore } from 'stores/GridStore';

import styles from './styles/Reports.module.less';

interface IState {
	pharmacies: string[];
	selectedAllPharmacies?: boolean;
	monthFrom?: Moment;
	monthTo?: Moment;
	year?: Moment;
	paymentDateFrom?: Moment;
	paymentDateTo?: Moment;
	fileType?: FileType;
}

export const GeneralPharmaciesBalanceReport = () => {
	const [visible, setVisible] = useState<boolean>(false);
	const [state, setState] = useState<IState>({
		pharmacies: [],
	});
	const [isBusy, setIsBusy] = useState<boolean>();

	const gridStore: IGridStore = useRootData((store) => store.gridStore);

	const { RangePicker } = DatePicker;

	const menu = (
		<Menu>
			<Menu.Item
				key="1"
				onClick={() => {
					setState({ ...state, fileType: FileType.CSV });
					setVisible(true);
				}}
			>
				Export data as CSV
			</Menu.Item>
			<Menu.Item
				key="2"
				onClick={() => {
					setState({ ...state, fileType: FileType.XLSX });
					setVisible(true);
				}}
			>
				Export data as XLSX
			</Menu.Item>
		</Menu>
	);

	const handleCancel = () => {
		setVisible(false);
		clearState();
	};

	const clearState = () => {
		setState({ pharmacies: [] });
		setIsBusy(false);
		gridStore.resetGrid('pharmaciesFinder');
	};

	const handleSubmit = async () => {
		const filters = gridStore.getGridFilters('pharmaciesFinder')!;

		setIsBusy(true);
		ajaxByUser('Successfully downloaded file', () =>
			getEssityApiClient().then((api) =>
				api.report
					.getGeneralBalance({
						year: state?.year?.year(),
						monthFrom: state?.monthFrom!.month() + 1,
						monthTo: state?.monthTo!.month() + 1,
						paymentDateFrom:
							state?.paymentDateFrom?.toDate() || undefined,
						paymentDateTo:
							state?.paymentDateTo?.toDate() || undefined,
						fileType: state?.fileType,
						pharmaciesIds: state?.pharmacies,
						isCheckedAll: state?.selectedAllPharmacies,
						sieveFilters: mapEssityFiltersToSieveFilters(filters),
					})
					.then((result) => {
						DownloadFile({
							response: result,
						});
					})
					.finally(() => {
						setVisible(false);
						clearState();
					})
			)
		);
	};

	const isExportDisabled = (): boolean => {
		return (
			state.monthFrom?.isAfter(state.monthTo) ||
			!state.monthTo ||
			!state.year ||
			(state.pharmacies?.length === 0 && !state.selectedAllPharmacies)
		);
	};

	return (
		<>
			<div className={styles.report}>
				<Space direction="vertical">
					<h3>Pharmacy balance report (general)</h3>

					<p>
						Export general pharmacy balance report to xlsx or csv
						file
					</p>
				</Space>

				<Dropdown overlay={menu} placement="bottomLeft" arrow>
					<Button type="primary" shape="round" size="middle">
						Run
					</Button>
				</Dropdown>
			</div>
			<IsolatedModal
				title="Pharmacy balance report (general)"
				visible={visible}
				onCancel={handleCancel}
			>
				<Space direction="vertical" className={styles.filters}>
					<PharmacyFinder
						readOnly={false}
						mode="multiple"
						validatePlaceId={false}
						style={{ width: 'auto' }}
						onPharmacySelect={(
							items: PharmacyDto[],
							selectedAll?: boolean
						) => {
							if (selectedAll) {
								setState({
									...state,
									selectedAllPharmacies: selectedAll,
								});
							} else {
								setState({
									...state,
									selectedAllPharmacies: false,
									pharmacies: items.map((x) => x.id!),
								});
							}
						}}
						customTitle={`Choose pharmacies (${state.selectedAllPharmacies
								? 'all'
								: state.pharmacies.length
							} selected)`}
					/>
					Settlement period
					<Space direction="horizontal">
						<EssityDatePicker
							picker="month"
							format={DateFormat.Month}
							placeholder="Month from"
							onChange={(date) =>
								setState({
									...state,
									monthFrom: date || undefined,
								})
							}
						/>
						<EssityDatePicker
							picker="month"
							format={DateFormat.Month}
							placeholder="Month to"
							onChange={(date) =>
								setState({
									...state,
									monthTo: date || undefined,
								})
							}
						/>
						<EssityDatePicker
							picker="year"
							format={DateFormat.Year}
							onChange={(date) =>
								setState({ ...state, year: date || undefined })
							}
						/>
					</Space>
					Payment date
					<RangePicker
						placeholder={['From', 'To']}
						value={[state.paymentDateFrom!, state.paymentDateTo!]}
						onChange={(value: any) => {
							setState({
								...state,
								paymentDateFrom: value ? value[0] : undefined,
								paymentDateTo: value ? value[1] : undefined,
							});
						}}
					/>
				</Space>
				<CenteredRow className={styles.exportButton}>
					{isBusy ? (
						<LoadingView message="Creating report, please wait..." />
					) : (
						<Button
							type="primary"
							shape="round"
							size="large"
							onClick={handleSubmit}
							disabled={isExportDisabled()}
						>
							Generate report
							<CloudDownloadOutlined />
						</Button>
					)}
				</CenteredRow>
			</IsolatedModal>
		</>
	);
};
